import React from 'react';
import { useLanguage } from '../../context/LanguageContext';

/**
 * La sesión cambió en otra pestaña: aviso bloqueante, sin recargar solo.
 *
 * ─── POR QUÉ ESTE COMPONENTE NO USA LOS TOKENS DEL TEMA ───────────────────────
 *
 * La primera versión sí los usaba, y salió **oscuro sobre oscuro** para quien
 * tiene el tema `dark`. La causa, medida: `--archon-bg-surface` existe y en
 * oscuro vale oscuro, pero **`--archon-text-primary` no existe en ninguna hoja
 * de estilo**, así que caía al valor de respaldo `#111`. Fondo oscuro, texto
 * casi negro. El aviso más importante de la aplicación, ilegible.
 *
 * La regla que se aplica: **un aviso de emergencia no puede depender del tema.**
 * Y la garantía no puede ser «acordarse de revisarlo», porque nadie se acuerda:
 *
 *   1. El diálogo **pinta su propio fondo opaco**, así que lo que haya detrás
 *      —cualquier tema, presente o futuro— es irrelevante por construcción.
 *   2. Los dos colores que deciden la legibilidad son **constantes de este
 *      fichero**, no variables heredadas. Un tema nuevo no puede cambiarlas
 *      porque no las conoce.
 *   3. `sessionChangedContrast.test.js` **calcula el ratio WCAG** entre esas
 *      constantes y falla por debajo de 4,5:1. Si alguien las toca sin mirar,
 *      la CI se pone roja.
 *
 * Es decir: no depende de que nadie se acuerde; depende de un número que se
 * comprueba en cada ejecución de la suite.
 */

//: Paleta del aviso. Deliberadamente literal: ver la nota de arriba.
//: Cualquier cambio aquí tiene que pasar por el test de contraste.
export const EMERGENCY_COLORS = {
  panelBg: '#ffffff',
  panelFg: '#1a1a1a',
  panelFgSoft: '#3d3d3d',
  // Gris neutro, no el azul de marca: R40 prohibe hex de marca en linea, y este
  // boton no necesita ser de marca — necesita leerse. 15,3:1 con el blanco.
  buttonBg: '#1f1f1f',
  buttonFg: '#ffffff',
  scrim: 'rgba(0, 0, 0, 0.72)',
};

export default function SessionChangedBlocker() {
  const { t } = useLanguage();

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="sesion-cambiada-titulo"
      data-testid="session-changed-blocker"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 20000,
        background: EMERGENCY_COLORS.scrim,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        style={{
          maxWidth: 460,
          background: EMERGENCY_COLORS.panelBg,
          color: EMERGENCY_COLORS.panelFg,
          borderRadius: 8,
          padding: 24,
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.45)',
        }}
      >
        <h2
          id="sesion-cambiada-titulo"
          style={{ fontSize: '1.15rem', marginBottom: 12, color: EMERGENCY_COLORS.panelFg }}
        >
          {t('session.changed.title', 'Your session changed in another tab')}
        </h2>
        <p style={{ marginBottom: 8, color: EMERGENCY_COLORS.panelFgSoft }}>
          {t(
            'session.changed.body',
            'Someone signed in with a different account in this browser. '
              + 'To avoid saving anything under the wrong identity, this tab has '
              + 'stopped sending requests.',
          )}
        </p>
        <p style={{ marginBottom: 20, color: EMERGENCY_COLORS.panelFgSoft }}>
          {t(
            'session.changed.hint',
            'Copy anything you have not saved, then reload the page.',
          )}
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            background: EMERGENCY_COLORS.buttonBg,
            color: EMERGENCY_COLORS.buttonFg,
            border: 'none',
            borderRadius: 6,
            padding: '10px 18px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {t('session.changed.reload', 'Reload page')}
        </button>
      </div>
    </div>
  );
}
